import { Component, EventEmitter, OnChanges, OnDestroy, OnInit, Output, Input, SimpleChanges } from '@angular/core';
import { GridApi, GridOptions, GridReadyEvent, ColDef, ColumnState } from 'ag-grid-community';
import { Observable, BehaviorSubject, Subject, Subscription, lastValueFrom, takeUntil } from 'rxjs';
import { AgGridOptionsBuilder, AgGridColumns, AgGridFilterParams } from '../../../Compliance/AgGrid';
import {
    AgGridMultiSelectTracker,
    AgGridMultiSelectCellRendererParams,
    AgGridMultiSelectRendererParams,
    AgGridMultiSelectedCellRenderer,
    AgGridMultiSelectedHeaderRenderer
 } from '../../../Compliance/AgGrid/MultiSelectTracker';
import { AgGridLinkCellRendererComponent, AgGridLinkCellRendererParams } from '../../../Compliance/AgGrid/CellRenderers/agGridLinkCellRenderer.component';
import { BusyIndicatorRef, BusyIndicatorService } from '../../../Busy-Indicator';
import { HelpService } from 'src/app/UI-Lib/Help-Tooltip';
import { UpgradeNavigationServiceHandler } from 'src/app/Common/Routing/upgrade-navigation-handler.service';
import { DeleteConfirmationComponent, DeleteConfirmationComponentParams } from 'src/app/Common/Delete-Confirmation/deleteConfirmation.component';
import { CurrencyPipe } from '@angular/common';
import { WeissmanModalService } from 'src/app/Compliance/WeissmanModalService';
import { PaymentBatchService } from '../../paymentBatch.service';
import { ICellRendererParamsForPaymentBatchPaymentsGridAction, PaymentBatchPaymentsGridActionCellRendererComponent } from './agGridActionCellRenderer.component';
import { MessageModalService } from 'src/app/UI-Lib/Message-Box/messageModal.service';
import { PaymentBatchRepository } from 'src/app/Core-Repositories/paymentBatch.repository';
import { TransmittalService } from 'src/app/Processing/Transmittal/transmittal.service';
import { PAYMENT_BATCH_DETAILS_HELP } from '../paymentBatchDetails.component.help';
import { chain, cloneDeep, findIndex, includes, reject } from 'lodash';
import * as _ from 'lodash';
import { ICellRendererParamsForPaymentBatchPaymentsGridPaymentErrors, PaymentBatchPaymentsGridPaymentErrorsCellRendererComponent } from './agGridPaymentErrorsCellRenderer.component';
import { WeissmanKeyValueDisplayPipe, WeissmanDateFormatPipe } from '../../../UI-Lib/Pipes';
import { WeissmanDateFormat } from '../../../UI-Lib/Pipes/Date-Format/date-formatting.pipe';
import { AgGridExportOptions, AgGridExportStartLRP } from '../../../Compliance/AgGrid/ToolPanel/models';
import { PaymentPackage } from '../../../Processing/Transmittal/transmittal.models';
import { FeatureFlagsService } from 'src/app/Common/FeatureFlags/feature-flags-service';
import {
    PaymentBatchPaymentsBulkUpdateModalComponent,
    PaymentBatchPaymentsBulkUpdateModalResult,
} from './Bulk-Update/paymentBatchPaymentsBulkUpdateModal.component';
import { PaymentBatchPaymentsBulkUpdateService } from './Bulk-Update/paymentBatchPaymentsBulkUpdate.service';
import { PaymentBatchPaymentsBulkUpdateResultModalComponent } from './Bulk-Update/paymentBatchPaymentsBulkUpdateResultModal.component';
import {
    PaymentApprovalActionModalComponent,
    PaymentApprovalActionModalParams,
} from '../../Approval/paymentApprovalActionModal.component';
import {
    PaymentApprovalCommentModalComponent,
    PaymentApprovalCommentModalParams,
} from '../../Approval/paymentApprovalCommentModal.component';
import { PaymentApprovalResultModalComponent } from '../../Approval/paymentApprovalResultModal.component';
import { DescriptorFormats, getDescriptorFormatDecimalPlaces } from '../../../Property-Characteristics/descriptor.model';

@Component({
    selector: 'payment-batch-payments-grid',
    templateUrl: 'paymentBatchPaymentsGrid.component.html',
    styles: [`
        :host { display: contents; }
    `],
    standalone: false
})

export class PaymentBatchPaymentsGridComponent implements OnInit, OnChanges, OnDestroy {
    constructor(
        private readonly _helpService: HelpService,
        private readonly _paymentBatchRepository: PaymentBatchRepository,
        private readonly _paymentBatchService: PaymentBatchService,
        public readonly currencyPipe: CurrencyPipe,
        private readonly _datePipe: WeissmanDateFormatPipe,
        private readonly _routerService: UpgradeNavigationServiceHandler,
        private readonly _modalService: WeissmanModalService,
        public readonly busyIndicatorService: BusyIndicatorService,
        private readonly _messageModalService: MessageModalService,
        private readonly _keyValueDisplayPipe: WeissmanKeyValueDisplayPipe,
        public readonly transmittalService: TransmittalService,
        private readonly _featureFlagsService: FeatureFlagsService,
        private readonly _bulkUpdateService: PaymentBatchPaymentsBulkUpdateService) {
    }

    @Input() paymentBatch: Core.PaymentBatchModel;
    @Input() workflowStatus: Core.PaymentBatchDetailsWorkflowStatusModel;
    @Output() gridDataLoadd: EventEmitter<void> = new EventEmitter();
    @Output() rowCount: EventEmitter<number> = new EventEmitter();

    isBulkDeleteVisible$: BehaviorSubject<boolean> = new BehaviorSubject(false);
    isBulkUpdateVisible$: BehaviorSubject<boolean> = new BehaviorSubject(false);
    gridId: System.Guid = '21DCC5BD-2F74-4422-BADD-ED31A7689AEB';
    busyRefId: string = this.busyIndicatorService.generateUniqueMessageIdentifier();
    paymentBatchId: number;
    totalsLoading: any;
    loadingGrid: boolean = false;
    enableClientPaymentApprover: boolean = false;
    hasApprovalSelection: boolean = false;

    readonly approvalStatusApproved = 1;
    readonly approvalStatusRejected = 2;
    readonly paymentStatusCompleted = 'Complete';

    filterWhereImApprover: boolean = false;

    get showApprovalButtons(): boolean {
        return this.enableClientPaymentApprover && (this.workflowStatus?.canPerformApprovalActions ?? false);
    }

    gridSearchModel: Core.PaymentBatchPaymentsGridSearchModel;
    gridResults: Core.PaymentBatchPaymentsGridFetchResults;

    gridTracker: AgGridMultiSelectTracker;

    gridOptions: GridOptions = new AgGridOptionsBuilder({
        suppressScrollOnNewData: true,
        isExternalFilterPresent: () => this.filterWhereImApprover && !!this.workflowStatus?.currentUserContactId,
        doesExternalFilterPass: (node) => node.data?.paymentApproverContactId === this.workflowStatus?.currentUserContactId,
        onFilterChanged: () => {
            this.gridTracker.onGridFilterChanged(),
            this._updateTotals();
        },
        onSortChanged: () => this.gridTracker.onGridSortChanged(),
        onGridColumnsChanged: () => {
            this.gridApi.sizeColumnsToFit();
        },
        onSelectionChanged: () => {
            this._updateTotals();
        },
        rowClassRules: {
            'ag-row-selected': (params) => params.data && this.gridTracker.isRowSelected(params.data.paymentId),
            'selected-totals-row': (params) => params.data && params.data.selectedTotalsRow
        },
        onModelUpdated: () => this.rowCount.emit(this.gridResults.entries.length),
    })
        .withContext(this)
        .withLoadingOverlay()
        .withColumnResize()
        //.withSort()
        .withMultipleColumnSort()
        .withTextSelection()
        .withColumnPinning()
        //.withVerticalScrollBar()
        .build();

    exportOptions: AgGridExportOptions = {
        start: async (columnsToReturn: Compliance.NameValuePair<string>[], fileFormat: Compliance.ExportFileFormatEnum): Promise<AgGridExportStartLRP> => {
            const searchModel = {
                paymentBatchId: this.paymentBatchId,
                // #585297: Keep CSV/Excel export consistent with the "Where I'm Approver" grid filter.
                // Backend re-resolves the current user's contact ID server-side, so this is safe to pass through.
                filterToCurrentUserAsApprover: this.filterWhereImApprover,
            };

            const lrp$ = this._paymentBatchRepository.startPaymentBatchDetailsExport({
                searchModel,
                columnsToReturn,
                fileFormat
            });

            const longRunningProcessId = await lastValueFrom(lrp$);
            const longRunningProcessTypeId = Compliance.LongRunningProcessTypeEnum.PaymentBatchDetailsExport;

            return { longRunningProcessId, longRunningProcessTypeId };
        },
        canCancel: true,
        getDisabled: () => {
            return !this.gridResults
                || this.gridResults.entries.length === 0
                || this.loadingGrid
                || this._noColumnsSelected();
        }
    };
    gridApi: GridApi;

    private _gridMultiSelectSub: Subscription;
    private _destroy$: Subject<void> = new Subject();
    private _busyRef: BusyIndicatorRef;
    private _paymentErrors: Core.BulkOperationResult[] = [];


    ngOnInit() {
        this._helpService.setContent(PAYMENT_BATCH_DETAILS_HELP);
        this.paymentBatchId = parseInt(this._routerService.getQuerystringParam('paymentBatchId'));
        this.enableClientPaymentApprover = this._featureFlagsService.featureFlags?.enableClientPaymentApprover ?? false;
        this.gridSearchModel = {
            paymentBatchId: this.paymentBatchId,
            pagination: {
                skip: 0,
                take: 2147483646,
                noPagination: true,
            }
        };
    }

    ngOnDestroy(): void {
        this._gridMultiSelectSub && this._gridMultiSelectSub.unsubscribe();
        this._destroy$.next();
        this._destroy$.complete();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['workflowStatus']
            && changes['workflowStatus'].previousValue == null
            && this.workflowStatus) {
            // "Where I'm Approver" always defaults to unchecked, regardless of approver-only mode.
            this.gridApi?.onFilterChanged();
        }
    }

    onFilterWhereImApproverChange(): void {
        this.gridApi?.onFilterChanged();
    }

    async onAgGridReady(event: GridReadyEvent): Promise<void> {
        this.gridApi = event.api;
        this.gridApi.setGridOption("loading", true);
        this.gridTracker = new AgGridMultiSelectTracker(this.gridOptions, this.gridApi, this._getGridRowIds.bind(this));

        this._gridMultiSelectSub = this.gridTracker.selectedRows$.subscribe(async () => {
            // if (!this.longRunningUpdateUnderway) {
            //     this.isBulkUpdateVisible$.next(this.gridTracker.hasSelectedRows());
            //     this.isRemoveExceptionsVisible$.next(this.gridTracker.hasSelectedRows());
            // }

            const isBulkDeleteVisible = this.workflowStatus?.canAddDeletePayments && this.gridTracker.hasSelectedRows();

            this.isBulkDeleteVisible$.next(isBulkDeleteVisible);
            const canBulkUpdate = this.workflowStatus?.canAdvanceTask ||
                (this.enableClientPaymentApprover && this.workflowStatus?.canAddDeletePayments);
            const isBulkUpdateVisible = canBulkUpdate && this.gridTracker.hasSelectedRows();

            this.isBulkDeleteVisible$.next(isBulkDeleteVisible);
            this.isBulkUpdateVisible$.next(isBulkUpdateVisible);
            const selectedRows = this._getSelectedRows();
            this.hasApprovalSelection = selectedRows.length > 0
                && !selectedRows.some(r => r.paymentStatus === this.paymentStatusCompleted);

            this._updateTotals();
        });

        // Now retrieve the data, create grid columns, and load the grid.
        await this.refresh();
    }

    addPaymentErrors(paymentErrors: Core.BulkOperationResult[]) {
        this._paymentErrors = paymentErrors;
    }

    async refresh(): Promise<void> {
        this._paymentErrors = [];

        return await this._loadData();
    }

    async bulkUpdate(): Promise<void> {
        await this._openBulkUpdateModal();
    }

    async openApprovalActionModal(targetStatusId: number, targetStatusLabel: string): Promise<void> {
        const paymentIds = this._getSelectedRows().map((x) => x.paymentId).filter((x) => x > 0);
        if (!paymentIds.length) {
            return;
        }

        const actionResult = await this._modalService.showAsync(
            PaymentApprovalActionModalComponent,
            {
                targetStatusId,
                targetStatusLabel,
                paymentCount: paymentIds.length,
            } as PaymentApprovalActionModalParams,
            'modal-md',
        );

        if (actionResult === null || actionResult === undefined) {
            return;
        }

        await this._executeBulkApproval(paymentIds, targetStatusId, actionResult.comment, targetStatusLabel);
    }

    async openApprovalCommentModal(): Promise<void> {
        const paymentIds = this._getSelectedRows().map((x) => x.paymentId).filter((x) => x > 0);
        if (!paymentIds.length) {
            return;
        }

        const commentResult = await this._modalService.showAsync(
            PaymentApprovalCommentModalComponent,
            { paymentCount: paymentIds.length } as PaymentApprovalCommentModalParams,
            'modal-md',
        );

        if (commentResult === null || commentResult === undefined) {
            return;
        }

        await this._executeBulkApproval(paymentIds, null, commentResult.comment, 'Update Approver Comment');
    }

    async onResetClick(): Promise<void> {
        const paymentIds = this._getSelectedRows().map((x) => x.paymentId).filter((x) => x > 0);
        if (!paymentIds.length) {
            return;
        }

        const confirmMessage = `Are you sure you want to reset approval information for ${paymentIds.length} selected payment(s)?`;

        try {
            await this._messageModalService.confirm(confirmMessage, 'Confirm Reset');
        } catch {
            // User canceled
            return;
        }

        this._showBusyIndicator('Reset', 'Resetting selected payments...', null, false, false);

        try {
            const bulkResult = await this._paymentBatchService.bulkResetClientApproval({ paymentIds });

            await this._hideBusyIndicator();
            await this._modalService.showAsync(
                PaymentApprovalResultModalComponent,
                { result: bulkResult, actionLabel: 'Reset', isReset: true },
                'modal-md',
            );

            if (bulkResult.successCount > 0) {
                await this.refresh();
                this.gridDataLoadd.emit();
            }
        } catch (error) {
            await this._hideBusyIndicator();
            throw error;
        }
    }

    private async _executeBulkApproval(paymentIds: number[], statusId: number | null, comment: string | null, actionLabel: string): Promise<void> {
        this._showBusyIndicator('Approval Update', 'Updating selected payments...', null, false, false);

        try {
            const bulkResult = await this._paymentBatchService.bulkUpdateClientApproval({
                paymentIds,
                paymentApprovalStatusId: statusId,
                paymentApprovalComment: comment,
            });

            await this._hideBusyIndicator();
            await this._modalService.showAsync(
                PaymentApprovalResultModalComponent,
                { result: bulkResult, actionLabel },
                'modal-md',
            );

            if (bulkResult.successCount > 0) {
                await this.refresh();
                this.gridDataLoadd.emit();
            }
        } catch (error) {
            await this._hideBusyIndicator();
            throw error;
        }
    }

    private async _openBulkUpdateModal(): Promise<void> {
        const paymentIds = this._getSelectedRows().map((x) => x.paymentId).filter((x) => x > 0);
        if (!paymentIds.length) {
            return;
        }

        const result = await this._modalService.showAsync(
            PaymentBatchPaymentsBulkUpdateModalComponent,
            {
                paymentIds,
                selectedCount: paymentIds.length,
                canRereview: !!this.workflowStatus?.canAdvanceTask,
                canChangeApprover: this.enableClientPaymentApprover && !!this.workflowStatus?.canAddDeletePayments && !this.workflowStatus?.isApproverOnlyMode,
            },
            'modal-lg',
        );

        if (!result) {
            return;
        }

        await this._applyBulkUpdate(result, paymentIds);
    }

    private async _applyBulkUpdate(result: PaymentBatchPaymentsBulkUpdateModalResult, paymentIds: number[]): Promise<void> {
        this._showBusyIndicator('Bulk Update Payments', 'Updating selected payments...', null, false, false);
        const outcome = await this._bulkUpdateService.execute({
            paymentBatchId: this.paymentBatchId,
            selection: this.gridTracker.getSelectedRowsModel(),
            paymentIds,
            update: result,
        });

        try {
            await this._hideBusyIndicator();
            await this._modalService.showAsync(
                PaymentBatchPaymentsBulkUpdateResultModalComponent,
                { outcome },
                'modal-md',
            );
            if (outcome.changed) {
                await this.refresh();
            }
        } catch (error) {
            await this._hideBusyIndicator();
            throw error;
        }
    }

    async bulkDelete(): Promise<void> {
        const params: DeleteConfirmationComponentParams = {
            item: 'Payment Batch Payments',
            message: 'Are you sure you want to remove selected Payments from the Batch?'
        };

        const result = await this._modalService.showAsync(DeleteConfirmationComponent, params, 'modal-md');

        if (result) {
            this._showBusyIndicator('Bulk Delete', 'Removing Payments from Batch', null, false, true);

            const model: Core.PaymentBatchDetailsPaymentBulkDeleteModel = {
                paymentBatchId: this.paymentBatchId,
                selectAllRows: this.gridTracker.getSelectedRowsModel().selectAllRows,
                selectedRows: this.gridTracker.getSelectedRowsModel().selectedRows
            };

            try {
                const longRunningProcessId = await this._paymentBatchService.startPaymentBatchDetailsPaymentBulkDelete(model);
                await this._busyRef.setLongRunningProcessId(longRunningProcessId);
            } catch (e) {
                await this._hideBusyIndicator();
                return Promise.reject(e);
            }
        }

        return Promise.resolve();
    }

    async deleteRow(params: ICellRendererParamsForPaymentBatchPaymentsGridAction): Promise<void> {
        const row = params.data as Core.PaymentBatchDetailsEntryModel;
        if (!row) {
            return;
        }

        const deleted = await this._deletePaymentBatchPaymentsRow(row);
        if (deleted) {
            this._refreshAndNotifyParent();
        }
    }

    canEdit(params) {
        return this.workflowStatus?.canAddDeletePayments;
    }

    canDelete() {
        return this.workflowStatus?.canAddDeletePayments;
    }

    getErrors(params: ICellRendererParamsForPaymentBatchPaymentsGridPaymentErrors): string[] {
        if (!params.data) {
            return [];
        }

        const row = params.data as Core.PaymentBatchDetailsEntryModel;
        const errorMessages = chain(this._paymentErrors)
            .filter({ entityID: row.paymentId })
            .map(error => _.values(_.pick(error, ['errorMessage', 'warningMessage'])))
            .flatten()
            .filter(message => message !== null && message !== undefined)
            .value() as string[];

        return errorMessages;
    }

    formatCurrency(value: number): string {
        if (value == null) {
            return '';
        }

        const formattedVal = this.currencyPipe.transform(Math.abs(value), 'USD', 'symbol-narrow');
        return value < 0 ? `(${formattedVal})` : formattedVal;
    }

    private async _deletePaymentBatchPaymentsRow(row: Core.PaymentBatchDetailsEntryModel): Promise<boolean> {
        try {
            await this._messageModalService.confirm('Are you sure you wish to remove the Payment from the Batch?', 'Confirm Delete');
        } catch (e) {
            return Promise.resolve(false);
        }

        const busyRef = this.busyIndicatorService.show({ message: 'Deleting' });

        try {
            await lastValueFrom(this._paymentBatchRepository.deletePaymentRow(row.paymentBatchId, row.paymentId));
        } finally {
            busyRef.hide();
        }

        return Promise.resolve(true);
    }

    private _showBusyIndicator(title: string, message: string = 'Working on it...', lrpId: number, canDismiss = true, hasProgressBar = true): void {
        if (this._busyRef) {
            this._busyRef.updateMessage(message, this.busyRefId);
            this._busyRef.setLongRunningProcessId(lrpId);
            return;
        }

        this._busyRef = this.busyIndicatorService.show({
            identifier: this.busyRefId,
            longRunningProcessId: lrpId,
            title: title ? title : 'Processing',
            message: message,
            hasProgressBar: hasProgressBar,
            canDismiss
        });

        this._busyRef.onProgressBarComplete().pipe(takeUntil(this._destroy$)).subscribe(async (success) => {
            await this._hideBusyIndicator();
            if (success) {
                this._refreshAndNotifyParent();
            }
        });
    }

    private async _hideBusyIndicator(): Promise<void> {
        if (this._busyRef) {
            await this._busyRef.hide();
            this._busyRef = null;
        }
        this._destroy$.next();
    }

    private _refreshAndNotifyParent() {
        this.refresh();
        this.gridDataLoadd.emit();
    }

    private _noColumnsSelected(): boolean {
        const visibleColumns = this.gridApi.getAllDisplayedColumns();
        const columnsWithData = reject(visibleColumns, col =>
            includes(['grid-column-multiselect', 'actions', 'paymentErrors'], col.getColId())
        );

        return !columnsWithData.length;
    }


    private async _loadData(): Promise<void> {
        this.loadingGrid = true;
        this.gridApi.setGridOption("loading", true);

        try {
            this.gridTracker.clear(false);
            this.isBulkDeleteVisible$.next(false);
            this.isBulkUpdateVisible$.next(false);
            this.hasApprovalSelection = false;

            this.gridResults = await this._paymentBatchService.getPaymentBatchPaymentsList(this.gridSearchModel);

            this._refreshRowData();
        } finally {
            this.gridApi.setGridOption("loading", false);
            this.loadingGrid = false;
        }
    }

    private _refreshRowData(): void {
        if (!this.gridApi) {
            return;
        }

        // Get current column states
        const colStates: ColumnState[] = this.gridApi.getColumnState();

        const columns = this.getGridColumns();

        this.gridApi.setGridOption('columnDefs', columns);

        // Restore prior column states
        this.gridApi.applyColumnState({ state: colStates, applyOrder: true });

        this.gridApi.setGridOption('rowData', this._getRowData());

        this._updateTotals();
    }

    private _getRowData(): Core.PaymentBatchDetailsEntryModel[] {
        return _.chain(this.gridResults.entries)
            .cloneDeep()
            .sortBy(['dueDate', 'paymentStatusSequence', 'parcelAcctNum'])
            .value();
    }

    private _updateTotals() : void {
        const selectedRows = this._getSelectedRows();

        if (selectedRows.length === 0) {
            this.gridApi.setGridOption('pinnedBottomRowData', []);
        }
        else {
            const model = {};
            const selectedEntry: any = {
                model,
                getModel: () => {
                    return model;
                },
                displayPipe: this._keyValueDisplayPipe,
                index: 0,
                paymentAmount: selectedRows.reduce((acc, y) => (acc += y.paymentAmount || 0), 0),
                installmentName: 'SELECTED',
                selectedTotalsRow: true,
            };

            this.gridApi.setGridOption('pinnedBottomRowData', [selectedEntry]);
        }
    }

    private _getGridRowIds(skip: number, take: number): Promise<Compliance.QueryResultModel<number>> {
        const model = { rowsToDisplay: [] };
        this.gridApi.forEachNodeAfterFilterAndSort(x => model.rowsToDisplay.push(x));
        const rows = model.rowsToDisplay.slice(skip, take + 1);
        const queryResultModel: Compliance.QueryResultModel<number> = {
            lastModifiedTimestamp: new Date(),
            totalRows: rows.length,
            totalValidRows: rows.length,
            data: rows.map((x) => {
                const entry = x.data as Core.PaymentBatchDetailsEntryModel;
                return entry && entry.paymentId;
            }),
        };

        return Promise.resolve(queryResultModel);
    }

    private _getSelectedRows(): Core.PaymentBatchDetailsEntryModel[] {
        const selectedRowsModel = this.gridTracker.getSelectedRowsModel();

        // If All is checked and no rows are unchecked, selectedRowsModel will have selectAllRows=true
        // and an empty selectedRows. Return all rows.
        // If All is checked and some rows are unchecked, selectedRowsModel will have selectAllRows=true
        // and selectedRows with the unchecked rows. Return all rows but what is in selectedRows.
        // If All is not checked and some rows are unchecked, selectedRowsModel will have selectAllRows=false
        // and selectedRows with the checked rows. Return all rows that are in selectedRows.
        return this.gridResults.entries.filter(x =>
            selectedRowsModel.selectAllRows && selectedRowsModel.selectedRows.indexOf(x.paymentId) === -1 ||
            !selectedRowsModel.selectAllRows && selectedRowsModel.selectedRows.indexOf(x.paymentId) !== -1);
    }



    getGridColumns(): ColDef[] {
        let cols = this._getDefaultColumns();

        if (this.gridResults.characteristics.length) {
            cols = [...cols, ...this._getPaymentBatchCharacteristicsColumns()];
        }

        cols.push(this._getPaymentErrorsColumn());
        cols.push(this._getActionsColumn());

        return cols;
    }

    private _getDefaultColumns(): ColDef[] {
        const cols: ColDef[] = [
            {
                colId: 'grid-column-multiselect',
                headerName: '',
                field: 'paymentId',
                width: AgGridColumns.selectionColumnWidth,
                suppressSizeToFit: true,
                suppressAutoSize: true,
                suppressColumnsToolPanel: true,
                editable: false,
                pinned: 'left',
                lockPinned: true,
                lockVisible: true,
                lockPosition: true,
                suppressMovable: true,
                headerComponent: AgGridMultiSelectedHeaderRenderer,
                headerComponentParams: { tracker: this.gridTracker } as AgGridMultiSelectRendererParams,
                cellRendererSelector: (params) => {
                    if (!params.data?.selectedTotalsRow) {
                        return {
                            component: AgGridMultiSelectedCellRenderer,
                            params: {
                                tracker: this.gridTracker,
                            } as AgGridMultiSelectCellRendererParams,
                        };
                    }
                },
            } as ColDef,
            {
                headerName: 'Payment Status',
                field: 'paymentStatus',
                width: AgGridColumns.textColumnWidth,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
            },
            {
                headerName: 'Payee',
                field: 'collector',
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                width: AgGridColumns.textColumnWidth,
                cellRenderer: AgGridLinkCellRendererComponent,
                cellRendererParams: {
                    getHelpContentId: (params: AgGridLinkCellRendererParams) => 'app.view-collector',
                    newWindow: true,
                    getLink: (params: AgGridLinkCellRendererParams) => {
                        const model = params.data as Core.PaymentBatchDetailsEntryModel;
                        if (!model) {
                            return '';
                        }
                        return `#/states/${model.stateId}/collectors/${model.collectorId}`;
                    },
                } as AgGridLinkCellRendererParams,
            },
            {
                headerName: 'State',
                field: 'state',
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                width: AgGridColumns.stateAbbreviationColumnWidth,
                cellRenderer: AgGridLinkCellRendererComponent,
                cellRendererParams: {
                    getHelpContentId: (params: AgGridLinkCellRendererParams) => 'app.view-state',
                    newWindow: true,
                    getLink: (params: AgGridLinkCellRendererParams) => {
                        const model = params.data as Core.PaymentBatchDetailsEntryModel;
                        if (!model) {
                            return '';
                        }
                        return `#/states/${model.stateId}`;
                    },
                } as AgGridLinkCellRendererParams,
            },
            {
                headerName: 'Parcel Acct Num',
                field: 'parcelAcctNum',
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                width: AgGridColumns.textColumnWidth,
                cellRenderer: AgGridLinkCellRendererComponent,
                cellRendererParams: {
                    getHelpContentId: (params: AgGridLinkCellRendererParams) => 'app.view-parcel',
                    newWindow: true,
                    getLink: (params: AgGridLinkCellRendererParams) => {
                        const model = params.data as Core.PaymentBatchDetailsEntryModel;
                        if (!model) {
                            return '';
                        }
                        return `#/parcel/${model.parcelId}`;
                    },
                } as AgGridLinkCellRendererParams,
            },
            {
                headerName: 'Installment',
                field: 'installmentName',
                width: AgGridColumns.textColumnWidth,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
            },
            {
                headerName: 'Payment Amount',
                field: 'paymentAmount',
                width: AgGridColumns.numericColumnWidth,
                filter: 'agNumberColumnFilter',
                filterParams: AgGridFilterParams.numberFilterWithBlankOptionsParams,
                floatingFilterComponentParams: AgGridFilterParams.numberFloatingFilterParams,
                valueFormatter: (x) => this.formatCurrency(x.value),
                cellClass: 'text-align-right',
                cellRenderer: AgGridLinkCellRendererComponent,
                cellRendererParams: {
                    getHelpContentId: (params: AgGridLinkCellRendererParams) => 'app.view-payment',
                    newWindow: true,
                    getLink: (params: AgGridLinkCellRendererParams) => {
                        const model = params.data as Core.PaymentBatchDetailsEntryModel;
                        if (!model) {
                            return '';
                        }
                        return `#/payment/${model.paymentId}`;
                    },
                    isDisabled: (params: AgGridLinkCellRendererParams) => params.data?.selectedTotalsRow,
                } as AgGridLinkCellRendererParams,
            },
            {
                headerName: 'Payment Due Date',
                field: 'dueDate',
                width: AgGridColumns.dateColumnWidth,
                filter: 'agDateColumnFilter',
                filterParams: AgGridFilterParams.dateFilterParamsWithBlankOptionsParams,
                floatingFilterComponentParams: AgGridFilterParams.dateFloatingFilterParams,
                valueFormatter: (params) => {
                    const model = params.data as Core.PaymentBatchDetailsEntryModel;
                    if (!(model && model.dueDate)) {
                        return '';
                    }

                    return WeissmanDateFormat(model.dueDate, true, 'stored');
                },
            },
            {
                headerName: 'Payment Notes',
                field: 'paymentNotes',
                width: AgGridColumns.textColumnWidth,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
            },
            {
                headerName: 'Payment Approver',
                field: 'paymentApprover',
                width: AgGridColumns.textColumnWidth,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterWithBlankOptionsParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                hide: true,
            },
            {
                headerName: 'Payment Approval Status',
                field: 'paymentApprovalStatus',
                width: AgGridColumns.textColumnWidth,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterWithBlankOptionsParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                hide: true,
            },
            {
                headerName: 'Payment Approval Change Date',
                field: 'paymentApprovalChangeDate',
                width: AgGridColumns.dateColumnWidth,
                filter: 'agDateColumnFilter',
                filterParams: AgGridFilterParams.dateFilterParamsWithBlankOptionsCentralTimeParams,
                floatingFilterComponentParams: AgGridFilterParams.dateFloatingFilterParams,
                hide: true,
                valueFormatter: (params) => {
                    const model = params.data as Core.PaymentBatchDetailsEntryModel;
                    if (!(model && model.paymentApprovalChangeDate)) {
                        return '';
                    }

                    // AC 8 (#572293): Payment Approval Change Date must display in US Central Time.
                    return WeissmanDateFormat(model.paymentApprovalChangeDate, true, 'central');
                },
            },
            {
                headerName: 'Payment Approval Comment',
                field: 'paymentApprovalComment',
                width: AgGridColumns.textColumnWidth,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterWithBlankOptionsParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                hide: true,
            },
            {
                headerName: 'Payment Type',
                field: 'paymentType',
                width: AgGridColumns.textColumnWidth,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                hide: true,
            },
            {
                headerName: 'Package No',
                field: 'transmittalPackageNum',
                width: AgGridColumns.numericColumnWidth,
                filter: 'agNumberColumnFilter',
                filterParams: AgGridFilterParams.numberFilterWithBlankOptionsParams,
                floatingFilterComponentParams: AgGridFilterParams.numberFloatingFilterParams,
                hide: true,
                // valueFormatter: (params) => {
                //     const model = params.data as Core.PaymentBatchDetailsEntryModel;
                //     if (!model) {
                //         return;
                //     }

                //     return this.paymentBatch.batchType !== Core.PaymentBatchTypeEnum.Transmittal ? ' ' : model.transmittalPackageNum;
                // },
                cellRenderer: AgGridLinkCellRendererComponent,
                cellRendererParams: {
                    getHelpContentId: (params: AgGridLinkCellRendererParams) => 'app.view-transmittal',
                    onClickAsync: async (params: AgGridLinkCellRendererParams) => {
                        const model = params.data as Core.PaymentBatchDetailsEntryModel;
                        if (!model) {
                            return;
                        }

                        const busyRef = this.busyIndicatorService.show({ message: 'Loading Transmittal...' });
                        let paymentPackage: PaymentPackage = null;
                        try {
                            paymentPackage = await this.transmittalService.GetPaymentPackageByID(model.paymentPackageId);
                            if (paymentPackage) {
                                this.transmittalService.showPaymentPackageReviewModal(paymentPackage);
                            }
                        } finally {
                            busyRef.hide();
                        }
                    },
                } as AgGridLinkCellRendererParams,
            },
            {
                headerName: 'Bill Amount',
                field: 'billAmount',
                width: AgGridColumns.numericColumnWidth,
                filter: 'agNumberColumnFilter',
                filterParams: AgGridFilterParams.numberFilterWithBlankOptionsParams,
                floatingFilterComponentParams: AgGridFilterParams.numberFloatingFilterParams,
                hide: true,
                valueFormatter: (x) => this.formatCurrency(x.value),
                cellClass: 'text-align-right',
            },
            {
                headerName: 'Base Payment Amount',
                field: 'basePaymentAmount',
                width: AgGridColumns.numericColumnWidth,
                filter: 'agNumberColumnFilter',
                filterParams: AgGridFilterParams.numberFilterWithBlankOptionsParams,
                floatingFilterComponentParams: AgGridFilterParams.numberFloatingFilterParams,
                hide: true,
                valueFormatter: (x) => this.formatCurrency(x.value),
                cellClass: 'text-align-right',
            },
            {
                headerName: 'Discount Amount',
                field: 'discountAmount',
                width: AgGridColumns.numericColumnWidth,
                filter: 'agNumberColumnFilter',
                filterParams: AgGridFilterParams.numberFilterWithBlankOptionsParams,
                floatingFilterComponentParams: AgGridFilterParams.numberFloatingFilterParams,
                hide: true,
                valueFormatter: (x) => this.formatCurrency(x.value),
                cellClass: 'text-align-right',
            },
            {
                headerName: 'Interest',
                field: 'interestAmount',
                width: AgGridColumns.numericColumnWidth,
                filter: 'agNumberColumnFilter',
                filterParams: AgGridFilterParams.numberFilterWithBlankOptionsParams,
                floatingFilterComponentParams: AgGridFilterParams.numberFloatingFilterParams,
                hide: true,
                valueFormatter: (x) => this.formatCurrency(x.value),
                cellClass: 'text-align-right',
            },
            {
                headerName: 'Penalty',
                field: 'penaltyAmount',
                width: AgGridColumns.numericColumnWidth,
                filter: 'agNumberColumnFilter',
                filterParams: AgGridFilterParams.numberFilterWithBlankOptionsParams,
                floatingFilterComponentParams: AgGridFilterParams.numberFloatingFilterParams,
                hide: true,
                valueFormatter: (x) => this.formatCurrency(x.value),
                cellClass: 'text-align-right',
            },
            {
                headerName: 'Exemption Amount',
                field: 'exemptionAmount',
                width: AgGridColumns.numericColumnWidth,
                filter: 'agNumberColumnFilter',
                filterParams: AgGridFilterParams.numberFilterWithBlankOptionsParams,
                floatingFilterComponentParams: AgGridFilterParams.numberFloatingFilterParams,
                hide: true,
                valueFormatter: (x) => this.formatCurrency(x.value),
                cellClass: 'text-align-right',
            },
            {
                headerName: 'Check No',
                field: 'checkNo',
                width: AgGridColumns.numericColumnWidth,
                filter: 'agNumberColumnFilter',
                filterParams: AgGridFilterParams.numberFilterWithBlankOptionsParams,
                floatingFilterComponentParams: AgGridFilterParams.numberFloatingFilterParams,
                hide: true,
            },
            {
                headerName: 'Check Date',
                field: 'checkDate',
                width: AgGridColumns.dateColumnWidth,
                filter: 'agDateColumnFilter',
                filterParams: AgGridFilterParams.dateFilterParamsWithBlankOptionsParams,
                floatingFilterComponentParams: AgGridFilterParams.dateFloatingFilterParams,
                hide: true,
                valueFormatter: (params) => {
                    const model = params.data as Core.PaymentBatchDetailsEntryModel;
                    if (!(model && model.checkDate)) {
                        return '';
                    }

                    return WeissmanDateFormat(model.checkDate, true, 'stored');
                },
            },
            {
                headerName: 'Payment Post Date',
                field: 'postDate',
                width: AgGridColumns.dateColumnWidth,
                filter: 'agDateColumnFilter',
                filterParams: AgGridFilterParams.dateFilterParamsWithBlankOptionsParams,
                floatingFilterComponentParams: AgGridFilterParams.dateFloatingFilterParams,
                hide: true,
                valueFormatter: (params) => {
                    const model = params.data as Core.PaymentBatchDetailsEntryModel;
                    if (!(model && model.postDate)) {
                        return '';
                    }

                    return WeissmanDateFormat(model.postDate, true, 'stored');
                },
            },
            {
                headerName: 'Tax Year',
                field: 'taxYear',
                width: AgGridColumns.numericColumnWidth,
                filter: 'agNumberColumnFilter',
                filterParams: AgGridFilterParams.numberFilterWithBlankOptionsParams,
                floatingFilterComponentParams: AgGridFilterParams.numberFloatingFilterParams,
                hide: true,
            },
            {
                headerName: 'Company',
                field: 'companyName',
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                width: AgGridColumns.textColumnWidth,
                hide: true,
                cellRenderer: AgGridLinkCellRendererComponent,
                cellRendererParams: {
                    getHelpContentId: (params: AgGridLinkCellRendererParams) => 'app.view-company',
                    newWindow: true,
                    getLink: (params: AgGridLinkCellRendererParams) => {
                        const model = params.data as Core.PaymentBatchDetailsEntryModel;
                        if (!model) {
                            return '';
                        }
                        return `#/company/${model.companyId}`;
                    },
                } as AgGridLinkCellRendererParams,
            },
            {
                headerName: 'Site',
                field: 'siteName',
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                width: AgGridColumns.textColumnWidth,
                hide: true,
                cellRenderer: AgGridLinkCellRendererComponent,
                cellRendererParams: {
                    getHelpContentId: (params: AgGridLinkCellRendererParams) => 'app.view-site',
                    newWindow: true,
                    getLink: (params: AgGridLinkCellRendererParams) => {
                        const model = params.data as Core.PaymentBatchDetailsEntryModel;
                        if (!model) {
                            return '';
                        }
                        return `#/site/${model.siteId}`;
                    },
                } as AgGridLinkCellRendererParams,
            },
            {
                headerName: 'Assessor',
                field: 'assessorName',
                width: AgGridColumns.textColumnWidth,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                cellRenderer: AgGridLinkCellRendererComponent,
                cellRendererParams: {
                    getHelpContentId: (params: AgGridLinkCellRendererParams) => 'app.view-assessor',
                    newWindow: true,
                    getLink: (params: AgGridLinkCellRendererParams) => {
                        const model = params.data as Core.PaymentBatchDetailsEntryModel;
                        if (!model) {
                            return '';
                        }
                        return `#/states/${model.stateId}/assessors/${model.assessorId}`;
                    },
                } as AgGridLinkCellRendererParams,
                // hide: true
            },
            {
                headerName: 'Site Pay Resp',
                field: 'sitePayResp',
                width: AgGridColumns.textColumnWidth,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                hide: true,
            },
            {
                headerName: 'Site Payment Resp',
                field: 'sitePaymentResp',
                width: AgGridColumns.textColumnWidth,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                hide: true,
            },
            {
                headerName: 'Parcel Pay Resp',
                field: 'parcelPayResp',
                width: AgGridColumns.textColumnWidth,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                hide: true,
            },
            {
                headerName: 'Parcel Payment Resp',
                field: 'parcelPaymentResp',
                width: AgGridColumns.textColumnWidth,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                hide: true,
            },
        ];

        return this.enableClientPaymentApprover
            ? cols
            : cols.filter((x) => !['paymentApprover', 'paymentApprovalStatus', 'paymentApprovalChangeDate', 'paymentApprovalComment'].includes(x.field as string));
    }

    private _getPaymentBatchCharacteristicsColumns(): ColDef[] {
        const duplicates = this.gridResults.characteristics.reduce((acc, x) => {
            acc[x.descriptorName] = _.has(acc, x.descriptorName);
            return acc;
        }, {});
        return this.gridResults.characteristics.map((x, i) => {
            let type = '';
            if (duplicates[x.descriptorName]) {
                if (x.entityTypeId === 5) {
                    type = 'Site';
                } else if (x.entityTypeId === 6) {
                    type = 'Parcel';
                }
            }

            let filter = 'agTextColumnFilter';
            let filterParams = AgGridFilterParams.textFilterWithBlankOptionsParams;
            let floatingFilterComponentParams = AgGridFilterParams.textFloatingFilterParams;
            let columnWidth: number = AgGridColumns.textColumnMedWidth;
            let valueFormatter: any;
            switch (x.descriptorFieldTypeId) {
                case Core.DescriptorFieldTypes.Number:
                case Core.DescriptorFieldTypes.Currency:
                    filter = 'agNumberColumnFilter';
                    filterParams = AgGridFilterParams.numberWithRangeAndEqualToAndBlankFilterParams;
                    floatingFilterComponentParams = AgGridFilterParams.numberFloatingFilterParams;
                    columnWidth = AgGridColumns.numericColumnWidth;
                    if (x.descriptorFieldTypeId == Core.DescriptorFieldTypes.Currency) {
                        valueFormatter = (params) => {
                            if (params.value === null || params.value === undefined || params.value === '') {
                                return '';
                            }
                            const numCurrency: number = parseFloat(params.value);
                            if (isNaN(numCurrency)) {
                                return '';
                            }
                            return this.formatCurrency(numCurrency);
                        };
                    } else {
                        const decimalPlaces = getDescriptorFormatDecimalPlaces(x.descriptorFormatId);
                        const useGrouping = x.descriptorFormatId !== DescriptorFormats.NoSeparators;
                        // Number.prototype.toLocaleString (rather than DecimalPipe) is used here because
                        // DecimalPipe has no built-in way to disable thousands grouping, which is needed
                        // for NoSeparators-formatted descriptors (e.g. Year Built). See #585811.
                        valueFormatter = (params) => {
                            if (params.value === null || params.value === undefined || params.value === '') {
                                return '';
                            }
                            const num = parseFloat(params.value);
                            if (isNaN(num)) {
                                return '';
                            }
                            return num.toLocaleString('en-US', {
                                minimumFractionDigits: decimalPlaces,
                                maximumFractionDigits: decimalPlaces,
                                useGrouping
                            });
                        };
                    }
                    break;
                case Core.DescriptorFieldTypes.Date:
                    filter = 'agDateColumnFilter';
                    filterParams = AgGridFilterParams.dateFilterParamsWithBlankOptionsParams;
                    floatingFilterComponentParams = AgGridFilterParams.dateFloatingFilterParams;
                    columnWidth = AgGridColumns.dateColumnWidth;
                    valueFormatter = (y) => {
                        if (!y.value) {
                            return '';
                        }

                        const d = new Date(y.value);
                        return this._datePipe.transform(d, true);
                    };
                    break;
                case Core.DescriptorFieldTypes.YesNo:
                    filterParams = AgGridFilterParams.booleanFilterWithBlankOptionsParams;
                    floatingFilterComponentParams = AgGridFilterParams.booleanFloatingFilterParams;
                    valueFormatter = (y) => {
                        return y.value.toLowerCase() === 'true' ? 'Yes' : y.value.toLowerCase() === 'false' ? 'No' : '';
                    };
                    break;
            }

            return {
                colId: `characteristic.${type ? `${type}.` : ''}${x.descriptorName.replace(/\s+/g, '')}`,
                headerName: `${type ? `${type} ` : ''}${x.descriptorName}`,
                field: `${type}.${x.descriptorName}`,
                toolPanelClass: 'Characteristics',
                hide: true,
                filter,
                filterParams,
                floatingFilterComponentParams,
                width: columnWidth,
                minWidth: AgGridColumns.textColumnSmallWidth,
                valueFormatter: valueFormatter,
                valueGetter: (y) => {
                    const entry = y.data as Core.ForecastGridEntry;
                    const rawValue = entry && entry.characteristicValues ? entry.characteristicValues[i] : '';

                    // Characteristic values are always transmitted as strings (see #PBPF_CharacteristicValues.Value
                    // VARCHAR(1000) in PaymentBatchPayments_Fetch.sql). agNumberColumnFilter requires valueGetter
                    // to return an actual number for its equals/notEqual/lessThan/greaterThan/inRange operators to
                    // work correctly - it does not parse numeric strings. See #585815.
                    if (x.descriptorFieldTypeId === Core.DescriptorFieldTypes.Number
                        || x.descriptorFieldTypeId === Core.DescriptorFieldTypes.Currency) {
                        if (rawValue === null || rawValue === undefined || rawValue === '') {
                            return null;
                        }

                        const numValue = parseFloat(rawValue);
                        return isNaN(numValue) ? null : numValue;
                    }

                    return rawValue;
                },
            };
        });
    }

    private _getPaymentErrorsColumn(): ColDef {
        return {
            headerName: '',
            field: 'paymentErrors',
            width: AgGridColumns.checkboxColumnMinWidth,
            suppressColumnsToolPanel: true,
            suppressSizeToFit: true,
            suppressAutoSize: true,
            resizable: false,
            sortable: false,
            cellRenderer: PaymentBatchPaymentsGridPaymentErrorsCellRendererComponent,
            cellRendererParams: {
                getErrors: this.getErrors.bind(this),
            } as ICellRendererParamsForPaymentBatchPaymentsGridPaymentErrors,
        };
    }

    private _getActionsColumn(): ColDef {
        return {
            headerName: '',
            field: 'actions',
            pinned: 'right',
            width: AgGridColumns.getActionColumnWidth(3),
            minWidth: AgGridColumns.getActionColumnWidth(3),
            maxWidth: AgGridColumns.getActionColumnWidth(3),
            lockPinned: true,
            suppressColumnsToolPanel: true,
            suppressSizeToFit: true,
            suppressAutoSize: true,
            resizable: false,
            sortable: false,
            cellRenderer: PaymentBatchPaymentsGridActionCellRendererComponent,
            cellRendererParams: {
                delete: this.deleteRow.bind(this),
                canDelete: this.canDelete.bind(this),
                canEdit: this.canEdit.bind(this),
            } as ICellRendererParamsForPaymentBatchPaymentsGridAction,
        };
    }
}

